import React, { useState, useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import Header from "@/components/Header";
import DateNavigation from "@/components/DateNavigation";
import WeeklyStatusTracker from "@/components/WeeklyStatusTracker";
import DailyWorkout from "@/components/DailyWorkout";
import BottomNavigation from "@/components/BottomNavigation";
import {
  getWeekDates,
  getISOWeek,
  normalizeDate,
  getDateString,
} from "@/lib/dateUtils";
import {
  CompleteWorkout,
  UserWeekSummary,
  WeeklyActivity,
  WorkoutSummary,
} from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";

const Home: React.FC = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  // Use reference to track the current date to avoid state update batching issues
  const [selectedDate, setSelectedDate] = useState(() => {
    // Use our normalizeDate utility for consistent date handling
    return normalizeDate(new Date());
  });
  // Get week dates starting from Monday
  const [weekDates, setWeekDates] = useState(() => getWeekDates(selectedDate));
  const [userId, setUserId] = useState<number | null>(null);
  const [currentWeek, setCurrentWeek] = useState(() => getISOWeek(new Date()));
  const [weeksWithWorkouts, setWeeksWithWorkouts] = useState<string[]>([]);

  // Use our utility to consistently format the selected date for display
  const selectedDateString = getDateString(selectedDate);

  // Define type for user data
  interface UserData {
    id: number;
    username: string;
    name: string;
  }

  // Fetch or create default user
  const { data: userData } = useQuery<UserData>({
    queryKey: ["/api/default-user"],
  });

  // Set user ID when data is loaded
  useEffect(() => {
    if (userData) {
      setUserId(userData.id);
    }
  }, [userData]);

  // Fetch week summary for joker/rest day tracking
  const { data: weekSummary } = useQuery<UserWeekSummary>({
    queryKey: ["/api/users", userId, "week-summary"],
    queryFn: async ({ queryKey }) => {
      const res = await fetch(`/api/users/${userId}?operation=week-summary`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch week summary");
      return res.json();
    },
    enabled: !!userId,
  });

  // Use state to directly manage the workout data
  const [currentWorkout, setCurrentWorkout] = useState<CompleteWorkout | null>(
    null
  );
  // Start with loading state as true by default
  const [isWorkoutLoading, setIsWorkoutLoading] = useState(true);

  // Fetch workout for a specific date
  const fetchWorkoutForDate = useCallback(
    async (date: Date, userId: number | null) => {
      if (!userId) return null;

      try {
        setIsWorkoutLoading(true);

        const res = await fetch(
          `/api/users/${userId}?operation=recommended-workout&date=${getDateString(
            date
          )}`,
          { credentials: "include" }
        );

        if (!res.ok) {
          throw new Error(`Failed to fetch: ${res.status}`);
        }

        const data = await res.json();

        // Check if we received a "no workout for past date" or "future date" response
        if ((data.isPastDate && !data.exists) || data.isFutureDate) {
          // No workout exists for this past date or it's a future date, return null to show appropriate message
          return null;
        }

        // Get the actual date from the workout data
        const workoutDate = data.date ? new Date(data.date) : date;

        // Add requested date information using the actual workout date
        data.requestedDate = workoutDate.toISOString();
        data.requestedDateString = workoutDate.toDateString();

        return data;
      } catch (error) {
        console.error("Error fetching workout:", error);
        if (error instanceof Error) {
          toast({
            title: "Error",
            description: `Could not load workout: ${error.message}`,
            variant: "destructive",
          });
        }
        return null;
      } finally {
        setIsWorkoutLoading(false);
      }
    },
    [toast, setIsWorkoutLoading]
  );

  // Generate workout only for today if needed
  const generateWorkoutForToday = useCallback(async () => {
    if (!userId) return;

    try {
      const results = [];
      let createdCount = 0;

      // Only handle current day, not the entire week
      const today = normalizeDate(new Date());

      // First check if a workout already exists for today
      const checkResponse = await fetch(
        `/api/users/${userId}?operation=recommended-workout&date=${getDateString(
          today
        )}&checkOnly=true`,
        { method: "GET", credentials: "include" }
      );

      if (!checkResponse.ok) {
        throw new Error(`Failed to check workout for today`);
      }

      const checkData = await checkResponse.json();

      // If workout already exists, add it to results without creating a new one
      if (checkData.exists) {
        results.push(checkData.workout);
        return { results, createdCount };
      }

      // If no workout exists for today, create one
      const response = await fetch(
        `/api/users/${userId}?operation=recommended-workout&date=${getDateString(
          today
        )}`,
        { method: "GET", credentials: "include" }
      );

      if (!response.ok) {
        throw new Error(`Failed to generate workout for today`);
      }

      const data = await response.json();
      results.push(data);
      createdCount = 1;

      // Mark today as having workouts generated
      const weekKey = `${currentWeek.year}-${currentWeek.weekNumber}`;
      if (!weeksWithWorkouts.includes(weekKey)) {
        setWeeksWithWorkouts((prev) => [...prev, weekKey]);
      }

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({
        queryKey: ["/api/users", userId, "workouts"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/users", userId, "weekly-activity"],
      });

      return { results, createdCount };
    } catch (error) {
      console.error("Error generating today's workout:", error);
      toast({
        title: "Error",
        description: `Failed to generate today's workout: ${
          error instanceof Error ? error.message : String(error)
        }`,
        variant: "destructive",
      });
      return null;
    }
  }, [
    userId,
    currentWeek,
    weeksWithWorkouts,
    setWeeksWithWorkouts,
    toast,
    queryClient,
  ]);

  // Check if we need to auto-generate workout for today
  useEffect(() => {
    if (!userId) return;

    const today = normalizeDate(new Date());

    // Only generate today's workout if not already done
    const todayKey = getDateString(today);

    // First check if a workout already exists for today
    const checkTodaysWorkout = async () => {
      try {
        // Use normalized today's date
        const normalizedToday = normalizeDate(new Date());
        const checkResponse = await fetch(
          `/api/users/${userId}?operation=recommended-workout&date=${getDateString(
            normalizedToday
          )}&checkOnly=true`,
          { method: "GET", credentials: "include" }
        );

        if (!checkResponse.ok) {
          console.error("Failed to check for today's workout");
          return;
        }

        const checkData = await checkResponse.json();

        // If workout already exists for today, do nothing
        if (checkData.exists) {
          return;
        }

        // If no workout exists for today, create one
        const response = await fetch(
          `/api/users/${userId}?operation=recommended-workout&date=${getDateString(
            normalizedToday
          )}`,
          { method: "GET", credentials: "include" }
        );

        if (!response.ok) {
          console.error("Failed to create today's workout");
          return;
        }
      } catch (error) {
        console.error("Error checking/creating today's workout:", error);
        return;
      }

      toast({
        title: "Today's Workout Created",
        description: "A new workout has been created for today",
        variant: "default",
      });

      // Refresh data
      queryClient.invalidateQueries({
        queryKey: ["/api/users", userId, "workouts"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/users", userId, "weekly-activity"],
      });
    };

    checkTodaysWorkout();
  }, [userId, queryClient, toast]);

  // Initial data load when component mounts
  useEffect(() => {
    const loadInitialData = async () => {
      if (userId && selectedDate) {
        // Only fetch on initial load
        const workoutData = await fetchWorkoutForDate(selectedDate, userId);
        if (workoutData) {
          setCurrentWorkout(workoutData);
        }
      }
    };

    loadInitialData();
  }, [userId, selectedDate, fetchWorkoutForDate]); // Include selectedDate and fetchWorkoutForDate

  // Fetch weekly activity for progress chart
  const { data: weeklyActivity } = useQuery<WeeklyActivity[]>({
    queryKey: ["/api/users", userId, "weekly-activity"],
    queryFn: async ({ queryKey }) => {
      const res = await fetch(
        `/api/users/${userId}?operation=weekly-activity`,
        {
          credentials: "include",
        }
      );
      if (!res.ok) throw new Error("Failed to fetch weekly activity");
      return res.json();
    },
    enabled: !!userId,
  });

  // Fetch recent workouts
  const { data: recentWorkouts } = useQuery<WorkoutSummary[]>({
    queryKey: ["/api/users", userId, "workouts"],
    queryFn: async ({ queryKey }) => {
      const res = await fetch(
        `/api/users/${userId}?operation=workouts&limit=3`,
        {
          credentials: "include",
        }
      );
      if (!res.ok) throw new Error("Failed to fetch recent workouts");
      return res.json();
    },
    enabled: !!userId,
  });

  // Date change handler with direct approach
  const handleDateChange = async (date: Date) => {
    if (!userId) {
      console.error("Cannot fetch workout - no user ID");
      return;
    }

    // Use our normalizeDate utility to ensure consistent date handling
    const newDate = normalizeDate(date);

    // Update date state immediately for UI consistency
    setSelectedDate(newDate);

    // Set the week dates starting with Monday
    setWeekDates(getWeekDates(newDate));

    // Check if we're in a different week and update current week if needed
    const newWeek = getISOWeek(newDate);
    if (
      newWeek.weekNumber !== currentWeek.weekNumber ||
      newWeek.year !== currentWeek.year
    ) {
      setCurrentWeek(newWeek);
    }

    // Then set loading state
    setIsWorkoutLoading(true);

    try {
      // Directly make the API call with updated URL
      const res = await fetch(
        `/api/users/${userId}?operation=recommended-workout&date=${getDateString(
          newDate
        )}`,
        { credentials: "include" }
      );

      if (!res.ok) {
        throw new Error(`Failed to fetch: ${res.status}`);
      }

      // Process the data
      const data = await res.json();

      // Check if we received a "no workout for past date" or "future date" response
      if ((data.isPastDate && !data.exists) || data.isFutureDate) {
        // No workout exists for this past date or it's a future date, set currentWorkout to null to show appropriate message
        setCurrentWorkout(null);
        return;
      }

      // Get the actual date from the workout data
      const workoutDate = data.date ? new Date(data.date) : newDate;

      // Add the date info for display using the workoutDate, not the requested date
      // This ensures we display the actual date the workout was created for
      data.requestedDate = workoutDate.toISOString();
      data.requestedDateString = workoutDate.toDateString();

      // Update workout data
      setCurrentWorkout(data);
    } catch (error) {
      console.error("Error in date change:", error);
      if (error instanceof Error) {
        toast({
          title: "Error",
          description: `Failed to load workout: ${error.message}`,
          variant: "destructive",
        });
      }
    } finally {
      setIsWorkoutLoading(false);
    }
  };

  // Update related data after workout changes
  const handleWorkoutUpdated = async () => {
    // Re-fetch the current workout
    const workoutData = await fetchWorkoutForDate(selectedDate, userId);
    if (workoutData) {
      setCurrentWorkout(workoutData);
    }

    // Also invalidate other related queries
    if (userId) {
      queryClient.invalidateQueries({
        queryKey: ["/api/users", userId, "week-summary"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/users", userId, "weekly-activity"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/users", userId, "workouts"],
      });
    }
  };

  return (
    <div
      id="app"
      className="max-w-md mx-auto min-h-screen bg-white shadow-lg relative"
    >
      <Header />

      <div className="px-4 py-2 bg-slate-50">
        <div className="flex justify-between items-center mb-2">
          <DateNavigation
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
            weekDates={weekDates}
          />
        </div>
      </div>

      {weekSummary && (
        <WeeklyStatusTracker
          jokerDaysTaken={Number(weekSummary.jokerDaysTaken) || 0}
          maxJokerDays={Number(weekSummary.maxJokerDays) || 2}
        />
      )}

      <div
        className="overflow-y-auto pb-20"
        style={{ maxHeight: "calc(100vh - 160px)" }}
      >
        {isWorkoutLoading ? (
          <div className="p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-gray-500">
              Loading workout for {new Date(selectedDate).toDateString()}...
            </p>
          </div>
        ) : currentWorkout ? (
          <>
            <div className="px-4 py-2 bg-blue-50 text-blue-800 text-sm">
              <p>
                Workout for{" "}
                <strong>
                  {currentWorkout.requestedDateString ||
                    new Date(currentWorkout.date).toDateString()}
                </strong>
              </p>
            </div>
            <DailyWorkout
              workout={currentWorkout}
              onWorkoutUpdated={handleWorkoutUpdated}
              weekSummary={weekSummary}
            />
          </>
        ) : (
          <div className="p-8 text-center">
            <p className="text-gray-500">
              {getDateString(selectedDate) < getDateString(new Date())
                ? "No workout exists for this past date."
                : getDateString(selectedDate) > getDateString(new Date())
                ? "Workouts can't be generated for future dates. Check back on this date."
                : "No workout found for this date."}
            </p>
          </div>
        )}
      </div>

      <BottomNavigation />
    </div>
  );
};

export default Home;
